import { useState, useEffect, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@app/providers/AuthProvider";
import { useLanguage } from "@shared/hooks/useLanguage";
import { useToast } from "@shared/components/Toaster";
import { cn } from "@shared/utils/cn";
import { usePublicStoreCategories } from "@shared/hooks/useStoreTypes";
import { apiClient } from "@/lib/api-client";

// ============================================
// Types
// ============================================

interface RegisterFormData {
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  prandName: string;
  note: string;
  acceptTerms: boolean;
}

interface ValidationErrors {
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  types?: string;
  acceptTerms?: string;
}

interface CityOption {
  id: string;
  nameAr: string;
  nameEn: string;
}

// ============================================
// Translations
// ============================================

const t = {
  title: { ar: "طلب إنشاء متجر", en: "Request a Store" },
  subtitle: {
    ar: "املأ البيانات وسنقوم بمراجعة طلبك والتواصل معك",
    en: "Fill in your details and we'll review your request and contact you",
  },
  fullName: { ar: "اسم صاحب المتجر", en: "Store Owner Name" },
  fullNamePlaceholder: { ar: "أحمد محمد", en: "Ahmed Mohamed" },
  phoneNumber: { ar: "رقم الهاتف", en: "Phone Number" },
  phoneNumberPlaceholder: { ar: "01xxxxxxxxx", en: "01xxxxxxxxx" },
  email: { ar: "البريد الإلكتروني (اختياري)", en: "Email (optional)" },
  emailPlaceholder: { ar: "owner@store.com", en: "owner@store.com" },
  storeName: { ar: "اسم المتجر التجاري", en: "Brand Name" },
  storeNamePlaceholder: {
    ar: "مطعم القاهرة الكبير",
    en: "Cairo Grand Restaurant",
  },
  address: { ar: "العنوان (اختياري)", en: "Address (optional)" },
  addressPlaceholder: {
    ar: "شارع رئيسي، رقم المبنى",
    en: "Main Street, Building Number",
  },
  city: { ar: "المدينة", en: "City" },
  selectCity: { ar: "اختر المدينة", en: "Select city" },
  loadingCities: { ar: "جاري تحميل المدن...", en: "Loading cities..." },
  storeTypes: { ar: "نوع المتجر", en: "Store Type" },
  selectTypes: {
    ar: "اختر نوع المتجر (يمكنك اختيار أكثر من نوع)",
    en: "Select store type(s)",
  },
  loadingTypes: { ar: "جاري تحميل الأنواع...", en: "Loading types..." },
  noTypesAvailable: {
    ar: "لا توجد أنواع متاحة حالياً",
    en: "No store types available",
  },
  note: { ar: "ملاحظات إضافية (اختياري)", en: "Additional Notes (optional)" },
  notePlaceholder: {
    ar: "أي معلومات إضافية تود إضافتها...",
    en: "Any additional information...",
  },
  acceptTerms: { ar: "أوافق على", en: "I agree to the" },
  termsOfService: { ar: "شروط الخدمة", en: "Terms of Service" },
  and: { ar: "و", en: "and" },
  privacyPolicy: { ar: "سياسة الخصوصية", en: "Privacy Policy" },
  submit: { ar: "إرسال الطلب", en: "Submit Request" },
  submitting: { ar: "جاري إرسال الطلب...", en: "Submitting request..." },
  successTitle: { ar: "تم إرسال الطلب بنجاح", en: "Request Submitted" },
  successMessage: {
    ar: "شكراً لك! سنقوم بمراجعة طلبك والتواصل معك قريباً عبر رقم الهاتف المسجل.",
    en: "Thank you! We'll review your request and contact you soon via the registered phone number.",
  },
  backToLogin: { ar: "العودة إلى تسجيل الدخول", en: "Back to Sign In" },
  closeAlert: { ar: "إغلاق", en: "Close" },
  validation: {
    fullNameRequired: { ar: "الاسم مطلوب", en: "Name is required" },
    fullNameMin: {
      ar: "الاسم يجب أن يكون حرفين على الأقل",
      en: "Name must be at least 2 characters",
    },
    phoneNumberRequired: {
      ar: "رقم الهاتف مطلوب",
      en: "Phone number is required",
    },
    phoneNumberInvalid: {
      ar: "يرجى إدخال رقم هاتف صحيح",
      en: "Please enter a valid phone number",
    },
    emailInvalid: {
      ar: "يرجى إدخال بريد إلكتروني صحيح",
      en: "Please enter a valid email",
    },
    typesRequired: {
      ar: "يرجى اختيار نوع متجر واحد على الأقل",
      en: "Please select at least one store type",
    },
    termsRequired: {
      ar: "يجب الموافقة على الشروط والأحكام",
      en: "You must accept the terms and conditions",
    },
  },
};

// ============================================
// Select classes
// ============================================

const selectClasses = cn(
  "w-full py-3 rounded-xl bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-100",
  "placeholder:text-surface-400 dark:placeholder:text-surface-500 border-2 transition-all duration-200",
  "disabled:opacity-50 disabled:cursor-not-allowed text-sm appearance-none",
  "border-transparent focus:border-primary-500 dark:focus:border-primary-400 focus:ring-4 focus:ring-primary-500/15",
);

// ============================================
// Component
// ============================================

export const RegisterPage: React.FC = () => {
  const { submitStoreRequest } = useAuth();
  const { currentLanguage } = useLanguage();
  const toast = useToast();

  const isAr = currentLanguage === "ar";
  const lang = (obj: { ar: string; en: string }) => (isAr ? obj.ar : obj.en);

  // Fetch store categories from API
  const { data: storeCategories = [], isLoading: isLoadingTypes } =
    usePublicStoreCategories();

  // Cities state
  const [cities, setCities] = useState<CityOption[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(true);

  const [formData, setFormData] = useState<RegisterFormData>({
    fullName: "",
    phoneNumber: "",
    email: "",
    address: "",
    city: "",
    prandName: "",
    note: "",
    acceptTerms: false,
  });
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {},
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [isSuccess, setIsSuccess] = useState(false);

  // ============================================
  // Fetch cities on mount
  // ============================================

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await apiClient.get<any>("/api/public/cities");

        // Handle wrapped response: { success: true, data: [...] }
        let items: any[] = [];
        if (response?.data && Array.isArray(response.data)) {
          items = response.data;
        } else if (Array.isArray(response)) {
          items = response;
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          items = response.data.data;
        }

        setCities(
          items
            .filter((item: any) => item.isActive !== false)
            .map((item: any) => ({
              id: item.id,
              nameAr: item.name || item.nameAr || "",
              nameEn: item.name || item.nameEn || "",
            })),
        );
      } catch (error) {
        console.error("Failed to fetch cities:", error);
        setCities([]);
      } finally {
        setIsLoadingCities(false);
      }
    };

    fetchCities();
  }, []);

  // ============================================
  // Handlers
  // ============================================

  const handleChange = (
    field: keyof RegisterFormData,
    value: string | boolean,
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors((prev) => {
        const u = { ...prev };
        delete u[field as keyof ValidationErrors];
        return u;
      });
    }
    if (serverError) setServerError(null);
  };

  const handleBlur = (field: string): void => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const toggleType = (typeId: string) => {
    setSelectedTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((t) => t !== typeId)
        : [...prev, typeId],
    );
    // Clear types error as soon as user selects/deselects
    setValidationErrors((prev) => {
      const updated = { ...prev };
      delete updated.types;
      return updated;
    });
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    if (!formData.fullName.trim())
      errors.fullName = lang(t.validation.fullNameRequired);
    else if (formData.fullName.trim().length < 2)
      errors.fullName = lang(t.validation.fullNameMin);

    if (!formData.phoneNumber.trim())
      errors.phoneNumber = lang(t.validation.phoneNumberRequired);
    else if (!/^01[0-25]\d{8}$/.test(formData.phoneNumber.trim()))
      errors.phoneNumber = lang(t.validation.phoneNumberInvalid);

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = lang(t.validation.emailInvalid);

    if (selectedTypes.length === 0)
      errors.types = lang(t.validation.typesRequired);

    if (!formData.acceptTerms)
      errors.acceptTerms = lang(t.validation.termsRequired);

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (): Promise<void> => {
    setTouchedFields({
      fullName: true,
      phoneNumber: true,
      acceptTerms: true,
      types: true,
    });

    // Extra guard for empty types
    if (selectedTypes.length === 0) {
      setValidationErrors((prev) => ({
        ...prev,
        types: lang(t.validation.typesRequired),
      }));
      setTouchedFields((prev) => ({ ...prev, types: true }));
      toast.error(
        isAr ? "يرجى اختيار نوع المتجر" : "Please select a store type",
      );
      return;
    }

    const isValid = validateForm();
    if (!isValid) {
      toast.error(
        isAr ? "يرجى تصحيح الحقول المطلوبة" : "Please fix the required fields",
      );
      return;
    }

    setIsSubmitting(true);
    setServerError(null);

    try {
      const payload = {
        name: formData.fullName.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        email: formData.email.trim() || undefined,
        address: formData.address.trim() || undefined,
        city: formData.city || undefined,
        prandName: formData.prandName.trim() || undefined,
        note: formData.note.trim() || undefined,
        types: selectedTypes,
      };

      await submitStoreRequest(payload);

      setIsSuccess(true);
      toast.success(lang(t.successTitle), {
        description: lang(t.successMessage),
      });
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (error instanceof Error
          ? error.message
          : lang(t.submitting) + " failed");
      setServerError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBaseClasses = (hasError: boolean) =>
    cn(
      "w-full py-3 rounded-xl bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-surface-100",
      "placeholder:text-surface-400 dark:placeholder:text-surface-500 border-2 transition-all duration-200",
      "disabled:opacity-50 disabled:cursor-not-allowed text-sm",
      hasError
        ? "border-error-400 dark:border-error-500 focus:border-error-500 focus:ring-4 focus:ring-error-500/15"
        : "border-transparent focus:border-primary-500 dark:focus:border-primary-400 focus:ring-4 focus:ring-primary-500/15",
    );

  // ============================================
  // Success State
  // ============================================
  if (isSuccess) {
    return (
      <div className="w-full animate-fade-in-up">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center animate-scale-in">
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12 text-success-500 dark:text-success-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
          </div>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-surface-900 dark:text-surface-100 mb-3">
            {lang(t.successTitle)}
          </h1>
          <p className="text-sm text-surface-500 dark:text-surface-400">
            {lang(t.successMessage)}
          </p>
        </div>
        <Link
          to="/auth/login"
          className="block w-full py-3 px-6 rounded-xl text-center text-white font-semibold text-sm bg-primary-600 hover:bg-primary-700 shadow-lg shadow-primary-600/25 transition-all duration-200 active:scale-[0.98]"
        >
          {lang(t.backToLogin)}
        </Link>
      </div>
    );
  }

  // ============================================
  // Form State
  // ============================================
  return (
    <div className="w-full animate-fade-in-up">
      <div
        className={cn(
          "mb-8 text-center",
          isAr ? "lg:text-right" : "lg:text-left",
        )}
      >
        <h1 className="text-2xl sm:text-3xl font-bold text-surface-900 dark:text-surface-100">
          {lang(t.title)}
        </h1>
        <p className="mt-2 text-sm text-surface-500 dark:text-surface-400">
          {lang(t.subtitle)}
        </p>
      </div>

      {serverError && (
        <div
          role="alert"
          className={cn(
            "mb-6 p-4 rounded-xl animate-fade-in bg-error-50 dark:bg-error-950/30 border border-error-200 dark:border-error-800/50 flex items-start gap-3",
          )}
        >
          <svg
            className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
            />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-error-700 dark:text-error-400">
              {serverError}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setServerError(null)}
            className="text-error-400 hover:text-error-500 flex-shrink-0"
            aria-label={lang(t.closeAlert)}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      <form
        onSubmit={(e) => e.preventDefault()}
        noValidate
        className="space-y-4"
      >
        {" "}
        {/* Full Name */}
        <div className="space-y-1.5">
          <label
            htmlFor="reg-name"
            className="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            {lang(t.fullName)}
          </label>
          <input
            id="reg-name"
            type="text"
            value={formData.fullName}
            onChange={(e) => handleChange("fullName", e.target.value)}
            onBlur={() => handleBlur("fullName")}
            placeholder={lang(t.fullNamePlaceholder)}
            disabled={isSubmitting}
            className={cn(
              inputBaseClasses(
                !!validationErrors.fullName && !!touchedFields.fullName,
              ),
              isAr ? "pr-4 pl-4" : "pl-4 pr-4",
            )}
          />
          {validationErrors.fullName && touchedFields.fullName && (
            <p className="text-xs text-error-500 dark:text-error-400 mt-1">
              {validationErrors.fullName}
            </p>
          )}
        </div>
        {/* Phone Number */}
        <div className="space-y-1.5">
          <label
            htmlFor="reg-phone"
            className="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            {lang(t.phoneNumber)}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-sm font-medium text-surface-500 dark:text-surface-400">
                +20
              </span>
            </div>
            <input
              id="reg-phone"
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                handleChange("phoneNumber", digits);
              }}
              onBlur={() => handleBlur("phoneNumber")}
              placeholder={lang(t.phoneNumberPlaceholder)}
              dir="ltr"
              disabled={isSubmitting}
              className={cn(
                inputBaseClasses(
                  !!validationErrors.phoneNumber && !!touchedFields.phoneNumber,
                ),
                "pl-12",
                isAr ? "pr-4" : "pr-4",
              )}
            />
          </div>
          {validationErrors.phoneNumber && touchedFields.phoneNumber && (
            <p className="text-xs text-error-500 dark:text-error-400 mt-1">
              {validationErrors.phoneNumber}
            </p>
          )}
          <p className="text-[10px] text-surface-400 dark:text-surface-500">
            {isAr
              ? "أدخل رقم هاتف مصري (11 أرقام بعد +20)"
              : "Enter Egyptian phone number (11 digits after +20)"}
          </p>
        </div>
        {/* Email (optional) */}
        <div className="space-y-1.5">
          <label
            htmlFor="reg-email"
            className="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            {lang(t.email)}
          </label>
          <input
            id="reg-email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            placeholder={lang(t.emailPlaceholder)}
            dir="ltr"
            disabled={isSubmitting}
            className={cn(
              inputBaseClasses(
                !!validationErrors.email && !!touchedFields.email,
              ),
              isAr ? "pr-4 pl-4" : "pl-4 pr-4",
            )}
          />
          {validationErrors.email && touchedFields.email && (
            <p className="text-xs text-error-500 dark:text-error-400 mt-1">
              {validationErrors.email}
            </p>
          )}
        </div>
        {/* Brand Name */}
        <div className="space-y-1.5">
          <label
            htmlFor="reg-brand"
            className="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            {lang(t.storeName)}
          </label>
          <input
            id="reg-brand"
            type="text"
            value={formData.prandName}
            onChange={(e) => handleChange("prandName", e.target.value)}
            placeholder={lang(t.storeNamePlaceholder)}
            disabled={isSubmitting}
            className={cn(
              inputBaseClasses(false),
              isAr ? "pr-4 pl-4" : "pl-4 pr-4",
            )}
          />
        </div>
        {/* City (select) + Address (text input) */}
        <div className="grid grid-cols-2 gap-3">
          {/* City Select */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
              {lang(t.city)}
            </label>
            <div className="relative">
              <select
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                disabled={isSubmitting || isLoadingCities}
                className={cn(
                  selectClasses,
                  isAr ? "pr-4 pl-10" : "pl-4 pr-10",
                )}
              >
                <option value="">
                  {isLoadingCities ? lang(t.loadingCities) : lang(t.selectCity)}
                </option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {isAr ? city.nameAr : city.nameEn}
                  </option>
                ))}
              </select>
              <div
                className={cn(
                  "absolute inset-y-0 flex items-center pointer-events-none",
                  isAr ? "left-0 pl-3" : "right-0 pr-3",
                )}
              >
                {isLoadingCities ? (
                  <svg
                    className="w-4 h-4 animate-spin text-surface-400"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-surface-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m19.5 8.25-7.5 7.5-7.5-7.5"
                    />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Address Input */}
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
              {lang(t.address)}
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
              placeholder={lang(t.addressPlaceholder)}
              disabled={isSubmitting}
              className={cn(
                inputBaseClasses(false),
                isAr ? "pr-4 pl-4" : "pl-4 pr-4",
              )}
            />
          </div>
        </div>
        {/* Store Types - Fetched from API */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
            {lang(t.storeTypes)}
          </label>
          <p className="text-xs text-surface-500 dark:text-surface-400">
            {lang(t.selectTypes)}
          </p>

          {isLoadingTypes ? (
            <div className="flex items-center gap-2 py-3">
              <svg
                className="w-4 h-4 animate-spin text-primary-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span className="text-xs text-surface-500 dark:text-surface-400">
                {lang(t.loadingTypes)}
              </span>
            </div>
          ) : storeCategories.length === 0 ? (
            <p className="text-xs text-surface-500 dark:text-surface-400 py-2">
              {lang(t.noTypesAvailable)}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {storeCategories.map((category) => {
                const isSelected = selectedTypes.includes(category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => toggleType(category.id)}
                    disabled={isSubmitting}
                    className={cn(
                      "px-3 py-1.5 rounded-xl text-sm font-medium border-2 transition-all duration-200",
                      isSelected
                        ? "bg-primary-100 dark:bg-primary-900/40 border-primary-500 text-primary-700 dark:text-primary-300"
                        : "bg-surface-100 dark:bg-surface-800 border-surface-200 dark:border-surface-700 text-surface-600 dark:text-surface-400 hover:border-primary-300",
                    )}
                  >
                    {isAr ? category.nameAr : category.nameEn}
                  </button>
                );
              })}
            </div>
          )}

          {validationErrors.types && touchedFields.types && (
            <p className="text-xs text-error-500 dark:text-error-400 mt-1">
              {validationErrors.types}
            </p>
          )}
        </div>
        {/* Note */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
            {lang(t.note)}
          </label>
          <textarea
            value={formData.note}
            onChange={(e) => handleChange("note", e.target.value)}
            placeholder={lang(t.notePlaceholder)}
            rows={3}
            disabled={isSubmitting}
            className={cn(
              inputBaseClasses(false),
              "resize-none",
              isAr ? "pr-4 pl-4" : "pl-4 pr-4",
            )}
          />
        </div>
        {/* Terms */}
        <label className="flex items-start gap-2.5 cursor-pointer select-none group">
          <input
            type="checkbox"
            checked={formData.acceptTerms}
            onChange={(e) => handleChange("acceptTerms", e.target.checked)}
            disabled={isSubmitting}
            className="sr-only"
          />
          <div
            className={cn(
              "w-5 h-5 rounded-md border-2 transition-all flex items-center justify-center flex-shrink-0 mt-0.5",
              formData.acceptTerms
                ? "bg-primary-600 border-primary-600"
                : "border-surface-300 dark:border-surface-600 group-hover:border-primary-400",
            )}
          >
            {formData.acceptTerms && (
              <svg
                className="w-3.5 h-3.5 text-white"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={3}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m4.5 12.75 6 6 9-13.5"
                />
              </svg>
            )}
          </div>
          <span className="text-sm text-surface-600 dark:text-surface-400">
            {lang(t.acceptTerms)}{" "}
            <a
              href="https://tamamapp.net/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              {lang(t.termsOfService)}
            </a>{" "}
            {lang(t.and)}{" "}
            <a
              href="https://tamamapp.net/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              {lang(t.privacyPolicy)}
            </a>
          </span>
        </label>
        {validationErrors.acceptTerms && touchedFields.acceptTerms && (
          <p className="text-xs text-error-500 dark:text-error-400">
            {validationErrors.acceptTerms}
          </p>
        )}
        {/* Submit */}
        <button
          type="button" // <-- critical change
          onClick={handleSubmit}
          disabled={isSubmitting || selectedTypes.length === 0}
          className={cn(
            "w-full py-3 px-6 rounded-xl text-white font-semibold text-sm bg-primary-600 hover:bg-primary-700 shadow-lg shadow-primary-600/25 transition-all duration-200 disabled:opacity-60 active:scale-[0.98] flex items-center justify-center gap-2.5",
            selectedTypes.length === 0 && "cursor-not-allowed",
          )}
        >
          {isSubmitting ? (
            <>
              <svg
                className="w-5 h-5 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              <span>{lang(t.submitting)}</span>
            </>
          ) : (
            <span>{lang(t.submit)}</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default RegisterPage;
